/**
 * Notification Context
 *
 * @module NotificationContext
 * @see docs/notifications.md
 *
 * Provides notification state and actions to the component tree.
 * Fetches initial notifications via REST and subscribes to real-time
 * updates via SSE.
 */

import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useRouterState } from '@tanstack/react-router';
import type { NotificationData } from './NotificationRegistry';
// Import renderer side-effects to register them
import './renderers';

import { API_URL } from '../../config';

/**
 * Check if a notification matches the currently viewed page,
 * so it can be auto-marked as read.
 */
function shouldAutoMarkRead(notification: NotificationData, pathname: string): boolean {
  if (notification.type !== 'workflow-run-complete') return false;

  const data = notification.data as { workflowId?: string };
  if (!data.workflowId) return false;

  // Match /flows/<workflowId> — any notification for this workflow is auto-read
  const match = pathname.match(/^\/flows\/([^/]+)$/);
  return match?.[1] === data.workflowId;
}

/** Payload of the `assistant-wake` SSE event — mirrors `AssistantWakeEvent['data']` in apps/api/src/events/broker.ts. */
export type AssistantWakeData = {
  conversationId: string;
  activityId: string;
  kind: 'flow_autobuild';
  workflowId: string;
  outcome: string;
  success: boolean;
  ts: string;
};

interface NotificationContextValue {
  notifications: NotificationData[];
  unreadCount: number;
  markRead: (id: string) => void;
  markAllRead: () => void;
  dismiss: (id: string) => void;
  /** Subscribe to `assistant-wake` events off the shared SSE stream. Returns an unsubscribe function. */
  onAssistantWake: (handler: (data: AssistantWakeData) => void) => () => void;
}

export const NotificationCtx = createContext<NotificationContextValue>({
  notifications: [],
  unreadCount: 0,
  markRead: () => {},
  markAllRead: () => {},
  dismiss: () => {},
  onAssistantWake: () => () => {},
});

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<NotificationData[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const eventSourceRef = useRef<EventSource | null>(null);

  const routerState = useRouterState({
    select: (s) => ({ pathname: s.location.pathname }),
  });
  const routerStateRef = useRef(routerState);
  routerStateRef.current = routerState;

  const notificationsRef = useRef(notifications);
  notificationsRef.current = notifications;

  // We need a ref to markRead so effects can call it without re-subscribing
  const markReadRef = useRef<(id: string) => void>(() => {});

  // Subscribers to the `assistant-wake` event — kept in a ref set so the SSE
  // effect below (mount-once) can dispatch to whichever consumers are
  // currently subscribed without re-subscribing itself.
  const assistantWakeSubscribersRef = useRef(new Set<(data: AssistantWakeData) => void>());

  const onAssistantWake = useCallback((handler: (data: AssistantWakeData) => void) => {
    assistantWakeSubscribersRef.current.add(handler);
    return () => {
      assistantWakeSubscribersRef.current.delete(handler);
    };
  }, []);

  // Fetch initial notifications
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await fetch(`${API_URL}/notifications?limit=50`, {
          credentials: 'include',
        });
        if (res.ok) {
          const body = await res.json();
          if (body.ok) {
            const data = body.data as NotificationData[];
            const { pathname } = routerStateRef.current;

            // Auto-mark notifications that match the current page
            const autoReadIds = new Set<string>();
            for (const n of data) {
              if (!n.read && shouldAutoMarkRead(n, pathname)) {
                autoReadIds.add(n.id);
              }
            }

            if (autoReadIds.size > 0) {
              setNotifications(
                data.map((n) => (autoReadIds.has(n.id) ? { ...n, read: true } : n))
              );
              setUnreadCount(Math.max(0, body.unreadCount - autoReadIds.size));
              // Fire API calls for each
              for (const id of autoReadIds) {
                markReadRef.current(id);
              }
            } else {
              setNotifications(data);
              setUnreadCount(body.unreadCount);
            }
          }
        }
      } catch (err) {
        console.error('Failed to fetch notifications', err);
      }
    };
    fetchNotifications();
  }, []);

  // SSE subscription
  useEffect(() => {
    const url = new URL(`${API_URL}/notifications/stream`);
    const eventSource = new EventSource(url.toString(), { withCredentials: true });
    eventSourceRef.current = eventSource;

    eventSource.addEventListener('new-notification', (event: MessageEvent) => {
      try {
        const notification = JSON.parse(event.data) as NotificationData;
        const { pathname } = routerStateRef.current;

        if (shouldAutoMarkRead(notification, pathname)) {
          // Add as already read — don't bump unread count
          setNotifications((prev) => [{ ...notification, read: true }, ...prev]);
          // Fire the API call to persist the read state
          markReadRef.current(notification.id);
        } else {
          setNotifications((prev) => [notification, ...prev]);
          setUnreadCount((prev) => prev + 1);
        }
      } catch (err) {
        console.error('Failed to parse notification event', err);
      }
    });

    eventSource.addEventListener('assistant-wake', (event: MessageEvent) => {
      try {
        const data = JSON.parse(event.data) as AssistantWakeData;
        for (const handler of assistantWakeSubscribersRef.current) handler(data);
      } catch (err) {
        console.error('Failed to parse assistant-wake event', err);
      }
    });

    eventSource.onerror = () => {
      // EventSource auto-reconnects, just log
      console.warn('Notification stream disconnected, reconnecting...');
    };

    return () => {
      eventSource.close();
      eventSourceRef.current = null;
    };
  }, []);

  // Auto-mark notifications as read when navigating to a matching flow page
  useEffect(() => {
    const { pathname } = routerState;
    const toMark: string[] = [];

    for (const n of notificationsRef.current) {
      if (!n.read && shouldAutoMarkRead(n, pathname)) {
        toMark.push(n.id);
      }
    }

    if (toMark.length === 0) return;

    const ids = new Set(toMark);
    setNotifications((prev) => prev.map((n) => (ids.has(n.id) ? { ...n, read: true } : n)));
    setUnreadCount((prev) => Math.max(0, prev - toMark.length));

    for (const id of toMark) {
      markReadRef.current(id);
    }
  }, [routerState.pathname]);

  const markRead = useCallback((id: string) => {
    fetch(`${API_URL}/notifications/read`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ id }),
    }).catch(console.error);
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
    setUnreadCount((prev) => Math.max(0, prev - 1));
  }, []);
  markReadRef.current = markRead;

  const markAllRead = useCallback(() => {
    fetch(`${API_URL}/notifications/read`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({}),
    }).catch(console.error);
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);

  const dismiss = useCallback((id: string) => {
    fetch(`${API_URL}/notifications/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    }).catch(console.error);
    setNotifications((prev) => {
      const removed = prev.find((n) => n.id === id);
      if (removed && !removed.read) {
        setUnreadCount((c) => Math.max(0, c - 1));
      }
      return prev.filter((n) => n.id !== id);
    });
  }, []);

  return (
    <NotificationCtx.Provider
      value={{ notifications, unreadCount, markRead, markAllRead, dismiss, onAssistantWake }}
    >
      {children}
    </NotificationCtx.Provider>
  );
}
